import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Volume2, Loader2, StopCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

// Global audio reference to allow stopping from anywhere
let currentAudio = null;

export const stopCurrentAudio = () => {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
};

export default function VoiceChatInput({ sessionId, onResponse, onTranscription, disabled }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      stopCurrentAudio();
    };
  }, []);

  const startRecording = async () => {
    // Stop any playing audio when user starts speaking
    stopCurrentAudio();
    setIsPlaying(false);
    
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Setup audio analyser for visualization
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      
      // Start level monitoring
      const updateLevel = () => {
        if (!analyserRef.current) return;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average / 255);
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
      
      // Setup media recorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Stop level monitoring
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        setAudioLevel(0);
        
        // Stop stream
        stream.getTracks().forEach(track => track.stop());
        
        // Process recorded audio
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      
      // Auto-stop after 30 seconds to prevent noise accumulation
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          console.log('Auto-stopping recording after 30 seconds');
          mediaRecorderRef.current.stop();
          setIsRecording(false);
        }
      }, 30000);
      
    } catch (err) {
      console.error('Recording error:', err);
      setError('Mikrofon-Zugriff verweigert. Bitte erlaube den Zugriff in deinen Browser-Einstellungen.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
      });
      reader.readAsDataURL(audioBlob);
      const audioBase64 = await base64Promise;
      
      // Send to voice chat endpoint
      const response = await axios.post(`${API_URL}/api/voice/chat`, {
        audio: audioBase64,
        session_id: sessionId
      });
      
      // Handle response
      if (response.data.success) {
        // Show transcription
        if (onTranscription) {
          onTranscription(response.data.user_text);
        }
        
        // Play audio response
        if (response.data.audio) {
          playAudioResponse(response.data.audio);
        }
        
        // Pass response data to parent
        if (onResponse) {
          onResponse({
            userText: response.data.user_text,
            botText: response.data.bot_text,
            showContactButton: response.data.show_contact_button,
            availableSlots: response.data.available_slots
          });
        }
      }
      
    } catch (err) {
      console.error('Processing error:', err);
      setError('Fehler bei der Verarbeitung. Bitte versuche es erneut.');
    } finally {
      setIsProcessing(false);
    }
  };

  const playAudioResponse = (audioBase64) => {
    // Stop any currently playing audio
    stopCurrentAudio();
    
    const audio = new Audio(`data:audio/mp3;base64,${audioBase64}`);
    currentAudio = audio;
    setIsPlaying(true);
    
    audio.onended = () => {
      setIsPlaying(false);
      currentAudio = null;
    };
    
    audio.onerror = () => {
      setIsPlaying(false);
      currentAudio = null;
    };
    
    audio.play().catch(err => {
      console.error('Audio playback error:', err);
      setIsPlaying(false);
      currentAudio = null;
    });
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid="voice-chat-input">
      {error && (
        <div className="text-red-500 text-sm text-center px-4">
          {error}
        </div>
      )}
      
      {/* Recording Button */}
      <button
        onClick={isRecording ? stopRecording : startRecording}
        disabled={disabled || isProcessing}
        className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all transform ${
          isRecording
            ? 'bg-red-500 hover:bg-red-600 scale-110'
            : isProcessing
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-blue-500 hover:bg-blue-600 hover:scale-105'
        } shadow-lg`}
        data-testid="record-button"
      >
        {/* Pulse animation when recording */}
        {isRecording && (
          <div 
            className="absolute inset-0 rounded-full bg-red-400 animate-ping"
            style={{ opacity: 0.3 + audioLevel * 0.4 }}
          />
        )}
        
        {isProcessing ? (
          <Loader2 className="h-8 w-8 text-white animate-spin" />
        ) : isRecording ? (
          <StopCircle className="h-8 w-8 text-white" />
        ) : (
          <Mic className="h-8 w-8 text-white" />
        )}
      </button>
      
      {/* Status Text */}
      <div className="text-sm text-gray-600 text-center">
        {isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Verarbeite...
          </span>
        ) : isRecording ? (
          <span className="flex items-center justify-center gap-2 text-red-600">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            Aufnahme läuft... Tippe zum Stoppen
          </span>
        ) : (
          <span>
            Tippe einmal, um zu sprechen 😊<br />
            Tippe nochmal, wenn du fertig bist – dann antworte ich dir.
          </span>
        )}
      </div>
      
      {/* Audio Level Indicator */}
      {isRecording && (
        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-400 to-red-500 transition-all"
            style={{ width: `${audioLevel * 100}%` }}
          />
        </div>
      )}
    </div>
  );
}
